import { useCallback, useMemo } from "react";
import { Avatar as MuiAvatar, Box } from "@mui/material";
import { ChatGroupAvatar } from "Components/Chat/ChatGroupAvatar";
import { ChannelPreviewMessenger, useChatContext } from "stream-chat-react";
import type {
  ChannelAvatarProps,
  ChannelPreviewUIComponentProps,
} from "stream-chat-react";
import { useChatChannelDisplayInfo } from "Hooks/Chat/useChatChannelDisplayInfo";
import { ChatRoleBadge } from "Components/Chat/ChatRoleBadge";
import type { AccessRoleCode } from "Models/AccessRoleCodes";
import { isCustomAttachment } from "Components/Chat/chatAttachmentTypes";
import type { CustomAttachmentType } from "Components/Chat/chatAttachmentTypes";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";

const ChatChannelAvatar = ({
  image,
  name,
  isOnline,
  isGroup,
  accessRole,
}: ChannelAvatarProps & {
  isOnline: boolean;
  isGroup: boolean;
  accessRole: AccessRoleCode | undefined;
}) => {
  const displayName = name ?? "";

  if (isGroup) {
    return (
      <Box position="relative" flexShrink={0}>
        <ChatGroupAvatar image={image ?? undefined} name={displayName} />
      </Box>
    );
  }

  return (
    <Box position="relative" flexShrink={0}>
      <MuiAvatar
        src={image ?? undefined}
        alt={displayName}
        sx={({ palette }) => ({
          width: 40,
          height: 40,
          bgcolor: image ? undefined : palette.primary.main,
        })}
      >
        {!image && displayName.charAt(0).toUpperCase()}
      </MuiAvatar>
      <ChatRoleBadge accessRole={accessRole} avatarSize={40} />
      <Box
        position="absolute"
        bottom={0}
        right={0}
        width={12}
        height={12}
        borderRadius="50%"
        border="2px solid"
        borderColor="background.paper"
        bgcolor={isOnline ? "success.main" : "grey.400"}
      />
    </Box>
  );
};

const PREVIEW_RESOURCES = Resources.Chat.Preview;

const previewTextByAttachmentType: Record<CustomAttachmentType, string> = {
  payment_request: PREVIEW_RESOURCES.PaymentRequest,
  payment_request_multi: PREVIEW_RESOURCES.PaymentRequest,
  direct_payment: PREVIEW_RESOURCES.DirectPayment,
};

export const ChatChannelPreview = (props: ChannelPreviewUIComponentProps) => {
  const { channel } = props;
  const { client } = useChatContext();
  const { t } = useResource();

  const {
    displayName,
    avatarImage,
    isGroup,
    isOnline,
    accessRole,
    isAnonymized,
  } = useChatChannelDisplayInfo(channel, client.userID);

  const avatarRenderer = useCallback(
    (avatarProps: ChannelAvatarProps) => (
      <ChatChannelAvatar
        {...avatarProps}
        // An anonymised channel shows NO picture, and that has to be stated rather than left to a
        // fallback: avatarProps.image is the library's own avatar, computed from the Stream
        // channel, so `avatarProps.image ?? avatarImage` would keep showing the real one.
        image={isAnonymized ? undefined : (avatarProps.image ?? avatarImage)}
        isOnline={isOnline}
        isGroup={isGroup}
        accessRole={accessRole}
      />
    ),
    [avatarImage, isOnline, isGroup, accessRole, isAnonymized],
  );

  const latestMessagePreview = useMemo(() => {
    const lastMessage = props.lastMessage;
    const customAttachment = lastMessage?.attachments?.find(isCustomAttachment);

    if (
      customAttachment &&
      "type" in customAttachment &&
      customAttachment.type
    ) {
      const resourceKey =
        previewTextByAttachmentType[
          customAttachment.type as CustomAttachmentType
        ];

      if (resourceKey) {
        const label = t(resourceKey);
        const amount = customAttachment.amount as number | undefined;
        const currencyCode = customAttachment.currencyCode as
          | string
          | undefined;

        if (amount && currencyCode) {
          return `${label} · ${formatCurrency({ amount, currencyCode })}`;
        }

        return label;
      }
    }

    return props.latestMessagePreview;
  }, [props.lastMessage, props.latestMessagePreview, t]);

  return (
    <ChannelPreviewMessenger
      {...props}
      // OURS wins, and the order is load-bearing: props.displayTitle is the library's own title,
      // derived from the Stream payload, which carries only the disclosable name (@handle for a
      // private persona — ADR-0007). displayName is the same title with the viewer-relative
      // upgrade applied, so preferring the library's would show @handle to the person's mutual followers.
      displayTitle={displayName || props.displayTitle}
      Avatar={avatarRenderer}
      latestMessagePreview={latestMessagePreview}
    />
  );
};
